import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';

/**
 * Interface for quality grade distribution data
 */
export interface QualityGradeData {
  grade: string;    // A, B, C, D
  count: number;
  percentage: number;
}

interface QualityGradeDistributionChartProps {
  data: QualityGradeData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Color mapping for quality grades
 */
const GRADE_COLORS: Record<string, string> = {
  A: '#10b981', // Green - Best quality
  B: '#3b82f6', // Blue - Good quality
  C: '#f59e0b', // Orange - Average quality
  D: '#ef4444', // Red - Poor quality
};

/**
 * Custom tooltip for quality grade pie chart
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-900">Grade {data.grade}</p>
        <p className="text-gray-700">
          Count: <span className="font-bold">{data.count}</span>
        </p>
        <p className="text-gray-700">
          Percentage: <span className="font-bold">{data.percentage.toFixed(1)}%</span>
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Quality Grade Distribution Pie Chart Component
 * Displays breakdown of cow dung quality grades (A/B/C/D)
 */
export const QualityGradeDistributionChart: React.FC<QualityGradeDistributionChartProps> = (
  {
    data,
    loading = false,
    error = null,
    height = 250,
  }
) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-gray-600">Loading quality distribution...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-red-600">Error: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-gray-500">No quality grade data available</p>
      </div>
    );
  }

  // Sort data by grade (A, B, C, D order)
  const sortedData = [...data].sort((a, b) => {
    const gradeOrder = { A: 1, B: 2, C: 3, D: 4 };
    return (gradeOrder[a.grade as keyof typeof gradeOrder] || 99) -
           (gradeOrder[b.grade as keyof typeof gradeOrder] || 99);
  });

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={sortedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ grade, percentage }) => `Grade ${grade} ${percentage.toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="count"
          >
            {sortedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={GRADE_COLORS[entry.grade] || '#6b7280'}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value, entry) => {
              const gradeData = entry.payload as QualityGradeData;
              return `Grade ${gradeData.grade} (${gradeData.count})`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default QualityGradeDistributionChart;
